import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserAvatar } from "./UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
    User,
    Settings,
    CreditCard,
    MessageSquare,
    LogOut,
    Trophy,
    Star,
    Users,
    Wallet,
    Package,
    Zap,
    Gift,
    MoreHorizontal,
    Moon,
    Lightbulb,
    ExternalLink,
    ChevronRight,
    ChevronLeft,
    Clock,
    HelpCircle,
    RefreshCcw,
    LayoutDashboard,
    Gamepad2,
    Calendar,
    Key,
    TrendingUp,
    CheckCircle,
    XCircle,
    Crown,
    ShoppingBag,
    Archive,
    Shield
} from "lucide-react";
import { Gems } from "./Gems";
import { useRef, useEffect, useState } from "react";
import { getAvailableMissions, getTodayMissions, getWeekMissions, getCreatorStats, getDeveloperStats } from "@/services/pointsService";
import { supabase } from "@/integrations/supabase/client";
import { type Mission } from "@/data/missions";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getXpForLevel } from "@/data/leveling";
import { LevelProgressBar } from "./LevelProgressBar";

interface UserSideMenuProps {
    userProfile: any;
    playerStats: any;
    creatorStats?: any;
    developerStats?: any;
    pendingRequestsCount?: number;
    signOut: () => void;
    onClose: () => void;
    navItems?: { label: string, path: string }[];
}

export const UserSideMenu = ({
    userProfile,
    playerStats,
    creatorStats: propsCreatorStats,
    developerStats: propsDeveloperStats,
    pendingRequestsCount: propsPendingRequestsCount,
    signOut,
    onClose,
    navItems
}: UserSideMenuProps) => {
    const navigate = useNavigate();
    const [missions, setMissions] = useState<Mission[]>([]);
    const [todayMissions, setTodayMissions] = useState<any[]>([]);
    const [loadingMissions, setLoadingMissions] = useState(true);
    const carouselRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (userProfile?.user_type === 'player') {
                try {
                    const [available, today, week] = await Promise.all([
                        getAvailableMissions(),
                        getTodayMissions(userProfile.id),
                        getWeekMissions(userProfile.id)
                    ]);

                    // Filter available missions to show only uncompleted daily and weekly ones
                    const todayCompletedIds = today.map(m => m.mission_id);
                    const weekCompletedIds = week.map(m => m.mission_id);

                    const filteredMissions = available.filter(m => {
                        if (m.type === 'daily') return !todayCompletedIds.includes(m.id);
                        if (m.type === 'weekly') return !weekCompletedIds.includes(m.id);
                        return false;
                    });

                    setMissions(filteredMissions.slice(0, 8));
                    setTodayMissions(today);
                } catch (error) {
                    console.error("Error fetching missions for side menu:", error);
                } finally {
                    setLoadingMissions(false);
                }
            } else {
                setLoadingMissions(false);
            }
        };

        fetchData();
    }, [userProfile?.id, userProfile?.user_type]);

    const scrollCarousel = (direction: 'left' | 'right') => {
        if (carouselRef.current) {
            const scrollAmount = 280;
            carouselRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleNavigation = (path: string) => {
        navigate(path);
        onClose();
    };

    const NavItem = ({ icon: Icon, label, path, badge, rightElement }: any) => (
        <button
            onClick={() => handleNavigation(path)}
            className="w-full flex items-center justify-between px-3 py-1 lg:py-3 text-sm md:text-base rounded-xl hover:bg-slate-700/20 transition-all group"
        >
            <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-card group-hover:bg-background transition-colors">
                    <Icon className="w-5 h-5 text-muted-foreground group-hover:text-foreground" />
                </div>
                <span className="font-medium text-muted-foreground group-hover:text-foreground">{label}</span>
            </div>
            <div className="flex items-center gap-2">
                {badge && (
                    <Badge variant="outline" className="h-5 min-w-[20px] px-1 flex items-center justify-center text-[10px]">
                        {badge}
                    </Badge>
                )}
                {rightElement}
                <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground" />
            </div>
        </button>
    );

    const getIconForLabel = (label: string) => {
        const lowerLabel = label.toLowerCase();
        if (lowerLabel.includes('dashboard')) return LayoutDashboard;
        if (lowerLabel.includes('browse') || lowerLabel.includes('games')) return Gamepad2;
        if (lowerLabel.includes('requests')) return Key;
        if (lowerLabel.includes('coverage')) return TrendingUp;
        if (lowerLabel.includes('statistics')) return TrendingUp;
        if (lowerLabel.includes('admin')) return Shield;
        return MoreHorizontal;
    };

    return (
        <div className="h-full flex flex-col bg-card/60 backdrop-blur-xl border-l border-white/5 w-full overflow-hidden min-w-0">
            <ScrollArea className="flex-1 w-full min-w-0">
                <div className="p-6 space-y-6 w-full max-w-full min-w-0 overflow-hidden">
                    {/* User Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <UserAvatar
                                src={userProfile?.avatar_url || undefined}
                                fallback={userProfile?.display_name || "U"}
                                size="lg"
                                borderStyle={userProfile?.resolvedBorderStyle}
                                className="shadow-xl"
                            />
                            <div>
                                <h2 className="text-2xl font-bold tracking-tight">{userProfile?.display_name?.replace(/^@/, '') || 'User'}</h2>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {userProfile?.user_type != "player" && (
                                        <Badge variant="outline" className="capitalize text-[10px] h-5 bg-white/5 border-white/10">
                                            {userProfile?.user_type || 'User'}
                                        </Badge>
                                    )}
                                    {/* Level Progress for Players */}
                                    {userProfile?.user_type === 'player' && playerStats && (
                                        <div className="flex-1 w-full">
                                            <LevelProgressBar
                                                xp={playerStats.xp}
                                                playerLevel={playerStats.player_level}
                                                small={true}
                                            />
                                        </div>
                                    )}
                                    {userProfile?.is_premium && (
                                        <Badge className="bg-gradient-to-r from-yellow-400 to-amber-600 text-[10px] h-5 border-none">
                                            PREMIUM
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats Encart for Players */}
                    {userProfile?.user_type === 'player' && playerStats && (
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <Card className="bg-slate-700/20 border-white/5 p-3 flex flex-col gap-1 items-start min-h-[80px] justify-center">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Gems size={14} with_text={false} />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Gems</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xl font-bold">{playerStats.coins}</span>
                                    {userProfile?.boost_end_date && new Date(userProfile.boost_end_date) > new Date() && (
                                        <Badge className="bg-yellow-500 text-black text-[9px] font-bold px-1.5 h-4 border-none shadow-sm animate-pulse">
                                            x2 Gems
                                        </Badge>
                                    )}
                                </div>
                            </Card>
                            <Card className="bg-slate-700/20 border-white/5 p-3 flex flex-col gap-1 items-start min-h-[80px] justify-center">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Gamepad2 className="w-3.5 h-3.5 text-indigo-400" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Games Tested</span>
                                </div>
                                <span className="text-xl font-bold">{playerStats.games_tested || 0}</span>
                            </Card>
                        </div>
                    )}


                    {/* Quick Stats Encart for Creators */}
                    {userProfile?.user_type === 'creator' && propsCreatorStats && (
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <Card className="bg-slate-700/20 border-white/5 p-3 flex flex-col gap-1 items-start min-h-[80px] justify-center">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Gamepad2 className="w-3.5 h-3.5 text-purple-400" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Games Covered</span>
                                </div>
                                <span className="text-xl font-bold">{propsCreatorStats.games_covered || 0}</span>
                            </Card>
                            <Card className="bg-slate-700/20 border-white/5 p-3 flex flex-col gap-1 items-start min-h-[80px] justify-center">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <TrendingUp className="w-3.5 h-3.5 text-orange-400" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Content Created</span>
                                </div>
                                <span className="text-xl font-bold">{propsCreatorStats.content_created || 0}</span>
                            </Card>
                        </div>
                    )}

                    {userProfile?.user_type === 'developer' && propsDeveloperStats && (
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <Card
                                onClick={() => handleNavigation('/dashboard')}
                                className="bg-slate-700/20 border-white/5 p-3 flex flex-col gap-1 items-start min-h-[100px] justify-center cursor-pointer hover:bg-slate-700/30 transition-all group"
                            >
                                <div className="flex items-center justify-between w-full text-muted-foreground group-hover:text-foreground transition-colors">
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Total Games</span>
                                    <Gamepad2 className="w-3.5 h-3.5" />
                                </div>
                                <span className="text-xl font-bold leading-none">{propsDeveloperStats.total_games}</span>
                                <span className="text-[10px] text-muted-foreground mt-0.5">Published</span>
                            </Card>

                            <Card
                                onClick={() => handleNavigation('/key-requests')}
                                className="bg-slate-700/20 border-white/5 p-3 flex flex-col gap-2 items-start min-h-[100px] justify-center cursor-pointer hover:bg-slate-700/30 transition-all group"
                            >
                                <div className="flex items-center justify-between w-full text-muted-foreground group-hover:text-foreground transition-colors">
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Requests</span>
                                    <Key className="w-3.5 h-3.5" />
                                </div>
                                <div className="flex flex-col gap-2 w-full">
                                    <span className="text-xl font-bold leading-none">{propsDeveloperStats.total_requests}</span>
                                    <div className="flex flex-wrap gap-1">
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-[9px] font-bold">
                                            <CheckCircle className="w-2.5 h-2.5" />
                                            <span>{propsDeveloperStats.approved_requests}</span>
                                        </div>
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 text-[9px] font-bold">
                                            <XCircle className="w-2.5 h-2.5" />
                                            <span>{propsDeveloperStats.rejected_requests}</span>
                                        </div>
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-400 text-[9px] font-bold">
                                            <Clock className="w-2.5 h-2.5" />
                                            <span>{propsDeveloperStats.pending_requests}</span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    )}



                    {/* Navigation Links for Mobile */}
                    {navItems && navItems.length > 0 && (
                        <div className="space-y-1">
                            {navItems
                                .filter(item => {
                                    const label = item.label.toLowerCase();
                                    const isAdmin = userProfile?.is_admin;

                                    // Remove redundant or unwanted links
                                    if (['my profile', 'beta ideas', 'dashboard'].includes(label)) return false;

                                    // Only show Admin to admins
                                    if (label === 'admin' && !isAdmin) return false;

                                    return true;
                                })
                                .map((item) => (
                                    <NavItem
                                        key={item.path}
                                        icon={getIconForLabel(item.label)}
                                        label={item.label}
                                        path={item.path}
                                    />
                                ))}
                        </div>
                    )}

                    {/* Missions Section */}
                    {userProfile?.user_type === 'player' && (
                        <div className="space-y-4 w-full min-w-0 overflow-hidden hidden md:block">
                            <div className="flex items-center justify-between px-1">
                                <div className="flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-blue-500" />
                                    <h3 className="font-semibold text-lg text-foreground">Missions</h3>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => scrollCarousel('left')}
                                        className="p-2 rounded-full bg-slate-700/20 hover:bg-slate-700/40 text-foreground transition-all flex items-center justify-center border border-white/10"
                                        aria-label="Previous mission"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => scrollCarousel('right')}
                                        className="p-2 rounded-full bg-slate-700/20 hover:bg-slate-700/40 text-foreground transition-all flex items-center justify-center border border-white/10"
                                        aria-label="Next mission"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="relative w-full overflow-hidden min-w-0">
                                <div
                                    ref={carouselRef}
                                    className="flex gap-4 overflow-x-auto scrollbar-none snap-x w-full min-w-0 max-w-[90vw] sm:max-w-[450px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                >
                                    {loadingMissions ? (
                                        [1, 2, 3].map(i => (
                                            <div key={i} className="min-w-[260px] h-24 bg-slate-700/10 rounded-2xl animate-pulse" />
                                        ))
                                    ) : missions.length > 0 ? (
                                        missions.map((mission) => (
                                            <Card
                                                key={mission.id}
                                                onClick={() => handleNavigation('/player-profile?tab=missions')}
                                                className="min-w-[260px] snap-center bg-slate-700/20 border-white/5 hover:border-white/10 transition-colors cursor-pointer group/card"
                                            >
                                                <CardContent className="p-4 flex flex-col justify-between h-full">
                                                    <div className="flex justify-between items-start">
                                                        <p className="text-sm font-medium leading-tight mr-4">{mission.description}</p>
                                                        <div className="flex items-center gap-1.5 text-xs font-bold text-primary min-w-fit">
                                                            <span>{mission.points_reward}</span>
                                                            <Gems size={14} with_text={false} />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))
                                    ) : (
                                        <p className="text-sm text-muted-foreground italic px-1">No pending missions available.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Links */}
                    <div className="space-y-1">
                        {userProfile?.user_type === 'creator' && (
                            <NavItem
                                icon={Key}
                                label="Key Requests"
                                path="/creator-profile?tab=requests"
                                badge={propsPendingRequestsCount && propsPendingRequestsCount > 0 ? propsPendingRequestsCount : undefined}
                            />
                        )}
                        {userProfile?.user_type === 'player' && (
                            <>
                                <NavItem
                                    icon={User}
                                    label="Profile"
                                    path="/player-profile"
                                />
                                <NavItem
                                    icon={ShoppingBag}
                                    label="Shop"
                                    path="/shop"
                                />
                                <NavItem
                                    icon={Archive}
                                    label="Inventory"
                                    path="/inventory"
                                />
                            </>
                        )}
                        <NavItem
                            icon={MessageSquare}
                            label="Messages"
                            path="/messages"
                            badge={playerStats?.unread_count > 0 ? playerStats.unread_count : undefined}
                        />
                        {userProfile?.user_type === 'developer' && (
                            <>
                                <NavItem
                                    icon={Crown}
                                    label="Buy Subscriptions"
                                    path="/subscriptions"
                                />
                                <NavItem
                                    icon={CreditCard}
                                    label="My Subscriptions"
                                    path="/my-subscriptions"
                                />
                            </>
                        )}
                        <NavItem icon={Settings} label="Settings" path="/settings" />
                    </div>

                </div>
            </ScrollArea>

            {/* Footer Actions */}
            <div className="p-4 bg-background/40 backdrop-blur-md border-t border-white/5">
                {/* Social Section */}
                <div className="space-y-4 pb-4">
                    <div className="flex flex-row gap-4 justify-between items-center">
                        <h3 className="text-md gradient-text font-bold text-muted-foreground uppercase tracking-wider px-1">Follow us</h3>
                        <div className="flex gap-4 px-1">
                            <a href="https://discord.gg/KNKfUWqe3j" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-slate-700/20 hover:bg-slate-700/40 transition-colors">
                                <svg className="w-4 h-4 text-blue-400 fill-current" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.003 14.003 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.23 10.23 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.196.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>
                            </a>
                            <a href="https://x.com/indiekeys_com" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-slate-700/20 hover:bg-slate-700/40 transition-colors">
                                <svg className="w-4 h-4 text-white fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                            </a>
                            <a href="https://www.youtube.com/@indie-keys" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-slate-700/20 hover:bg-slate-700/40 transition-colors">
                                <svg className="w-4 h-4 text-red-500 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                            </a>
                        </div>
                    </div>
                </div>
                <div className={`grid ${userProfile?.user_type === 'player' ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                    <Button
                        variant="outline"
                        onClick={() => handleNavigation('/help')}
                        className="w-full hover:text-white h-auto py-3 flex-col gap-2 rounded-2xl bg-slate-700/10 border-white/5 hover:bg-slate-700/20"
                    >
                        <HelpCircle className="w-5 h-5 text-blue-400" />
                        <span className="text-[10px] font-medium uppercase tracking-wider">Help</span>
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => handleNavigation('/beta-ideas')}
                        className="w-full hover:text-white h-auto py-3 flex-col gap-2 rounded-2xl bg-slate-700/10 border-white/5 hover:bg-slate-700/20"
                    >
                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                        <span className="text-[10px] font-medium uppercase tracking-wider">Beta Ideas</span>
                    </Button>
                    {userProfile?.user_type === 'player' && (
                        <Button
                            variant="outline"
                            onClick={() => handleNavigation('/ranking')}
                            className="w-full hover:text-white h-auto py-3 flex-col gap-2 rounded-2xl bg-slate-700/10 border-white/5 hover:bg-slate-700/20"
                        >
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <span className="text-[10px] font-medium uppercase tracking-wider">Rankings</span>
                        </Button>
                    )}
                </div>
                <Button
                    variant="ghost"
                    onClick={signOut}
                    className="w-full mt-4 h-11 text-red-500 hover:bg-red-500/10 hover:text-red-500 rounded-xl gap-2 text-sm font-semibold"
                >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                </Button>
            </div>
        </div>
    );
};
